import { useContext, useMemo, useState } from 'react';
import { Button, Tooltip } from '@fluentui/react-components';
import { LibraryRegular, OpenRegular } from '@fluentui/react-icons';

import { SignInContext } from '@modules/users';
import { TItem, SelectItemDialog, TSelectItemDialogState } from '@modules/items';
import { TDocument, TDraft, TVersion } from '../../../types';
import { useSetTaggedBlock } from '../../../api';
import { DialogPickerField } from '@shared/components';
import { TERMINOLOGY, DEFAULT_OPTION_LABEL } from '@shared/i18n';
import { filterTagsForDisplay, findSelectedTaggedBlock, buildHref } from '@shared/utils';
import useIsVersionEditable from '../../../hooks/useIsVersionEditable.hook';
import DocumentContext from '../../../contexts/DocumentContext.context';

type TProps = {
  item: TDocument;
  versionId: string;
  version: TVersion | TDraft;
};

const TaggedSnippets = ({ item, versionId, version }: TProps) => {
  const { organization } = useContext(SignInContext);
  const { resolvedItems } = useContext(DocumentContext);
  const setTaggedBlock = useSetTaggedBlock();

  const [selectDialogState, setSelectDialogState] = useState<TSelectItemDialogState | undefined>(undefined);
  const [activeTagId, setActiveTagId] = useState<string | null>(null);

  const selectedTaggedBlocks = version.selectedTaggedBlocks;
  const taggedBlocksRoot = item.taggedBlocksRoot ?? item.blocksRoot;

  // Show active tags + deactivated tags with existing selections
  const tagsToDisplay = useMemo(() => {
    const selectedTagIds = new Set(selectedTaggedBlocks.map((stb) => stb.blockTagId));
    return filterTagsForDisplay(organization.blockTags ?? [], selectedTagIds);
  }, [organization.blockTags, selectedTaggedBlocks]);

  const isEditable = useIsVersionEditable(versionId, item);

  const handleOpenSelect = (blockTagId: string) => {
    setActiveTagId(blockTagId);
    setSelectDialogState({
      isOpen: true,
      onSelect: async (selectedItems: TItem[]) => {
        const selectedBlock = selectedItems[0];
        if (!selectedBlock) return;
        await setTaggedBlock.mutateAsync({
          id: item._id,
          blockTagId,
          blockId: selectedBlock._id,
          blockItem: selectedBlock,
        });

        setSelectDialogState(undefined);
      },
    });
  };

  if (tagsToDisplay.length === 0) return null;

  return (
    <>
      {tagsToDisplay.map((tag) => {
        const isDeactivated = !tag.active;
        const selectedBlock = findSelectedTaggedBlock(selectedTaggedBlocks, tag.id);
        const blockName = selectedBlock ? item.blocksData[selectedBlock.blockId]?.itemName : undefined;
        // Block could not be resolved - may be archived, deleted, or inaccessible
        const isBlockUnavailable = selectedBlock && !blockName;
        // When a specific version is frozen (completed documents), append it to the display value.
        const displayValue = blockName
          ? selectedBlock?.blockVersionId
            ? `${blockName} (v${selectedBlock.blockVersionId})`
            : blockName
          : isBlockUnavailable
            ? 'Unknown'
            : undefined;

        // Deactivated tags are always read-only
        // Disable while mutation is pending to prevent rapid clicks
        const isFieldEditable = isEditable && !isDeactivated && !setTaggedBlock.isPending;

        // Check if the selected block is from outside this document's resolved picker root
        const resolvedBlockPath = selectedBlock ? resolvedItems[selectedBlock.blockId]?.item?.path : undefined;
        const isOutsideResolvedRoot =
          selectedBlock &&
          !isBlockUnavailable &&
          taggedBlocksRoot &&
          resolvedBlockPath &&
          !resolvedBlockPath.includes(taggedBlocksRoot);

        // Determine warning message
        let warning: string | undefined;
        if (isDeactivated) {
          warning = 'This tag has been deactivated.';
        } else if (isOutsideResolvedRoot) {
          warning = `This ${TERMINOLOGY.inlineBlock.lower} is from a different product.`;
        } else if (isBlockUnavailable) {
          warning = `Selected ${TERMINOLOGY.inlineBlock.lower} is archived.`;
        }

        const blockHref = selectedBlock
          ? buildHref(`/inline-block/${selectedBlock.blockId}`, { view: selectedBlock.blockVersionId })
          : undefined;

        return (
          <DialogPickerField
            key={tag.id}
            label={tag.label}
            value={displayValue ?? ''}
            placeholder={DEFAULT_OPTION_LABEL}
            disabled={!isFieldEditable}
            onClick={() => handleOpenSelect(tag.id)}
            icon={<LibraryRegular />}
            warning={warning}
            action={
              blockHref && (
                <Tooltip content={`Open ${TERMINOLOGY.inlineBlock.lower}`} relationship="label">
                  <Button as="a" href={blockHref} target="_blank" appearance="subtle" icon={<OpenRegular />} />
                </Tooltip>
              )
            }
          />
        );
      })}

      <SelectItemDialog
        initialFolderId={taggedBlocksRoot}
        initialSelectedItems={(() => {
          const sel = activeTagId ? findSelectedTaggedBlock(selectedTaggedBlocks, activeTagId) : undefined;
          if (!sel) return [];
          const resolved = resolvedItems[sel.blockId];
          if (!resolved) return [];
          return [resolved.item];
        })()}
        state={selectDialogState}
        setState={setSelectDialogState}
        title={`Select ${TERMINOLOGY.inlineBlock.lower}`}
        actionText="Select"
        navigatableFolderTypes={['BLOCK']}
        selectableTypes={['INLINE_BLOCK']}
        getDisabledReason={(selectItem) => {
          // Only check tags for inline blocks, not folders
          if (selectItem.type !== 'INLINE_BLOCK') return undefined;
          if (selectItem.stats?.archivedDate) return `This ${TERMINOLOGY.inlineBlock.lower} is archived`;
          if (!selectItem.blockTagId) return `This ${TERMINOLOGY.inlineBlock.lower} has no tag assigned`;
          if (selectItem.blockTagId !== activeTagId) return `This ${TERMINOLOGY.inlineBlock.lower} has a different tag`;
          return undefined;
        }}
      />
    </>
  );
};

export default TaggedSnippets;
